import { ComposerNotesPageProps, NotesDataBlock } from './notes';
import {
  VideoWatchStatus,
  VideoChapter,
  UpNext,
  Sidebar,
  VideoSourceData,
  Sections,
  Transcript,
  Sources,
  VideoNavItem,
  TreeNode,
  TreeNodeChild,
  SourceArgType,
  OverridesArgType,
  FirstAidBlockData,
  LearnData,
} from './learndb';
import { OnProgressProps } from 'react-player/base';
import { Playlist } from './playlist';
import { ReactNode } from 'react';
import { SubProduct } from './user';
import { NodeDataField, NodeTag } from './node';
import { NotePage } from './pages';
import { CksChapter } from './csk_books';
import { LearnCompletedPlaylist } from './featuredPlaylist';
import { IntlShape } from 'react-intl';

export type PageType = 'video' | 'notes';

export type BreadcrumbV2Object = {
  breadcrumbs: string[];
  link: string;
  l2Icon?: string;
  title?: string;
};

export type TrackBreadcrumbHandler = (
  breadcrumbName?: string,
  link?: string,
) => Promise<void>;

export const firstAidBooks = [
  'f14',
  'f15',
  'f16',
  'f17',
  'f18',
  'f19',
  'f20',
  'f21',
  'f22',
  'f23',
  'f24',
] as const;

export type BookProps = { ref: string; group: string[]; primary?: boolean };

export type YotZNodeDataProps = {
  youTubeID: string;
  title: string;
  thumbnail: string;
  description: string;
};

export type CompleteAnatomyNodeDataProps = {
  links: string[];
  title: string;
};

export type YotzProps = YotZNodeDataProps & {
  url: string;
  typeName: string;
  typeLogo: string;
  data?: string[];
};

export type Progress = {
  attempted: number;
  attemptedNodes: number[];
  available: number;
  correct: number;
  correctNodes: number[];
  progressPct: number;
};

export type Guideline = {
  name: string;
  link: string;
};

export type GuidelinesObject = {
  us: Guideline[];
  uk: Guideline[];
};

export type LearningObjective = {
  originData: string;
  html: string;
};

export type TranscriptHtml = {
  html: string;
};

export type CaptionSource = {
  src: string;
  srcLang: string;
};

export type Note = {
  doc: string;
  page: string;
};

export type Notes = {
  2018?: Note;
  2019?: Note;
  2020?: Note;
  2021?: Note;
};

export type CMECredit = {
  URL: string;
  claimID: number;
  completedAt: string;
  createdAt: string;
  credits: number;
  initialViews: number;
  mediaID: number;
  name: string;
  nodeID: number;
  status: 'completed' | 'registered' | 'unregistered';
  type: 'video' | 'quiz';
  updatedAt: string;
  userID: number;
  views: number;
};

export type CME = {
  attempted: number;
  credits: CMECredit[];
  enabled: boolean;
  info: {
    learnNodeID: number;
  };
  total: number;
};

export type Contributor = {
  id: number;
  link?: string;
  name: string;
};

export type Citation = {
  author: Contributor[];
  editor: Contributor[];
  illustrator: Contributor[];
  reviewer: Contributor[];
  voiceover: Contributor[];
  artist: Contributor[];
};

export type ExamProductKey = 'DO' | 'MD';

export type StageProductKey = ExamProductKey | 'PA' | 'NP';

export type ExamStageProduct = {
  key: string;
  name: string | ((intl: IntlShape) => string);
  image: string;
  description: string;
  color: string;
  stageRecommendations: string[];
  videoRecommendations: string[];
}[];

export type ProductVideos = {
  [key: string]: LearnPagesData;
};

export type OnboardingVideoStep = {
  learnNodeID: number;
  name: string;
  imageURL: string;
  duration: string;
  url: string;
  watchStatus: VideoWatchStatus;
};

export type VideoStepType = OnboardingVideoStep & {
  heading: ReactNode;
  isCompleted: boolean;
};

type TwitterCard = 'summary' | 'summary_large_image' | 'player' | 'app';

export type MetadataProps = {
  title: string;
  descr?: string;
  type?: string;
  image?: string;
  card?: TwitterCard;
  url?: string;
  imageWidth?: string;
  video?: string;
  videoUrl?: string;
  videoSecureUrl?: string;
  videoType?: string;
  audio?: string;
  author?: string;
  robots?: string;
  publishedTime?: string;
  modifiedTime?: string;
};

export type LearnMetadata = MetadataProps & {
  learnUrl: string;
  videoTitle: string;
  reviewStructuredData: null | string; // ?
  medicalWebPageStructuredData: string;
  breadcrumbStructuredData: string;
  videoObjectStructuredData: string;
  questionStructuredData: string;
  lastReviewed?: string;
  lastUpdated?: string;
};

export type Tag = {
  tagID: number;
  tag: string;
};

export type AccessState = {
  [key: string]: SubProduct;
};

export type TranscriptData = {
  data: { srcLang: string };
  learnID: number;
  nodeID: number;
  tagID: number;
  text: string;
};

export enum DenyReason {
  loggedOut = 'loggedOut',
  expired = 'expired',
  lockedProduct = 'lockedProduct',
}

export type LockedSubProduct = SubProduct & { id: string };

export type LearnPagesData = {
  access: string[];
  breadcrumbsv2: BreadcrumbV2Object[];
  citation: {
    citations: Citation;
    created: string;
    learnName: string;
  };
  cme: {
    enabled: boolean;
  };
  concept?: string;
  content: {
    hasNotes?: Notes;
    hasVideo: boolean;
  };
  composerNotes?: ComposerNotesPageProps;
  sidebar?: Sidebar;
  created: string | null;
  data: VideoSourceData;
  fcCount: number;
  icons: string[];
  isPrimeOnly?: null | 'prime-only';
  learnNodeData: {
    access: string[];
    citation: Citation;
    notes: Notes;
    overrides?: {
      allowLoggedOutViewing?: 'false';
    };
  };
  learnNodeID: number;
  libraryLink: string | null;
  loggedInLibraryLink: string | null;
  loggedOutLibraryLink: string | null;
  mediaID: number;
  modified: string;
  name: string;
  names: string[];
  notes?: Notes;
  pageName: string;
  quesCount: number;
  overrides?: {
    allowLoggedOutViewing?: 'true' | 'false';
  };
  renames: string[];
  restricted: boolean;
  sections: Sections;
  status: number;
  subtype: number;
  success?: number;
  term: string;
  thumb: string;
  type: number;
  tags: Tag[];
  url: string;
  upNext: UpNext | boolean;
  views: number;
  watchStatus: VideoWatchStatus;
  transcripts?: Transcript[];
  chapters?: VideoChapter[];
  sources?: Sources;
  isLockedOut?: boolean;
};

export type LearnDataVideo = LearnData & {
  overriddenDisplayNames?: { [productId: string]: string };
  isAnswerPage?: boolean;
  isVideoPage?: boolean;
};

export type LearnNotesPropsDatabase = {
  contents: {
    contents: string;
    time: number;
  };
  noteID: number;
};

export type LearnNotesProps = {
  contents: string;
  time?: number;
  noteID: number;
};

export type RelatedVideo = {
  $: string;
  $$: string;
  anatomy: boolean;
  breadcrumbv2: BreadcrumbV2Object[];
  data: VideoSourceData;
  duration: number;
  learnNodeID: number;
  mediaNodeID: number;
  name: string;
  pageType: string;
  path: string;
  prime: boolean;
  questionSets: string[];
  resources: string[];
  thumb: string;
  url: string;
};

export type LearnPrimaryData = {
  addr: string;
  addrs: string[];
  aliases: string[];
  breadcrumbv2: BreadcrumbV2Object;
  created: string | null; // ?
  isPrimeOnly: boolean;
  learnNodeID: number;
  mediaID: number;
  modified: string;
  name: string;
  nodeData: {
    access: string[];
    citation?: {
      citations: {
        author: Contributor[];
        editor: Contributor[];
        illustrator: Contributor[];
        reviewer: Contributor[];
        voiceover: Contributor[];
      };
      lastUpdated: string;
    };
    notes: {
      [index: string]: string;
    };
  };
  notes: {
    [index: string]: {
      doc: number;
      page: number;
    };
  };
  status: 0 | 1 | 2;
  subject: string;
  views: number;
};

export enum VideoType {
  banner,
  dailyVideoLimitReached,
  video,
}

export type SrcType = 'hls' | 's3' | 'yt';

export type SrcDataProps = {
  type?: SrcType;
  src: string;
};

export type VideoComponentProps = {
  srcData?: SrcDataProps;
  poster: string;
  mediaNodeID?: LearnData['mediaID'];
  captions?: CaptionSource[];
  chapters?: VideoChapter[];
  playlists?: Playlist[];
  dailyVideoLimitReached?: boolean;
  onProgress: (state: OnProgressProps) => void;
  onEnded: (state: OnProgressProps) => void;
  overlayComponents?: ReactNode[];
  mediaID: LearnDataVideo['mediaID'];
  learnNodeID: LearnDataVideo['learnNodeID'];
  name: LearnDataVideo['name'];
  isPrimeOnly: LearnDataVideo['isPrimeOnly'];
  breadcrumbs: LearnDataVideo['breadcrumbsv2'];
  autoplay?: boolean;
  isEmbed?: boolean;
  showOAIButton?: boolean;
  classNames?: {
    controlBar?: string;
  };
};

export interface LocalizedTitles {
  [languages: string]: string;
}

export enum LearnNodeTags {
  Step1 = 1,
  Step2 = 2,
  Recall = 25,
}

export function getLearnNodeTagName(enumValue: number) {
  const keys = Object.keys(LearnNodeTags).filter(
    (x) => LearnNodeTags[x as keyof typeof LearnNodeTags] == enumValue,
  );

  return keys.length > 0 ? keys[0] : null;
}

export interface VideoTreeNode extends TreeNode {
  video: VideoNavItem;
}

export interface TopicTreeNode extends TreeNode {
  displayName: string;
  icon?: string;
  navID?: number;
  groupName: string;
  contains?: TreeNodeChild[];
}

export interface LearnCitation {
  learnName: string;
  lastUpdated: Date;
  created: Date;
  citations: Record<string, { id: number; name?: string }[]>;
}

export interface Caption {
  src: string;
  srcLang: string;
}

export interface LearnCacheEntityData {
  script?: string | null;
  muxToken?: string;
  mux?: string;
  hls?: string;
  s3?: string;
  yt?: string;
  by?: string;
  dur?: string;
  $?: string;
  $$?: string;
  muxThumbSmall?: string;
  muxThumb?: string;
  thumb?: string;
  vc?: string;
  captions?: Caption[];
  views?: number;
  progress?: number;
  lastTime?: number;
  objects?: Record<string, string>;
}

export interface LearnCacheEntity {
  learnNodeID: number;
  learnNodeData: Partial<NodeDataField>;
  type: number;
  subtype: number;
  status: number;
  created: Date;
  modified: Date;
  mediaID: number;
  pageName?: string;
  thumb?: string;
  name?: string;
  tags?: NodeTag[];
  lastmod?: Date;
  url?: string;
  notes?: Notes;
  composerNotes?: NotePage;
  definition?: string;
  summary?: string;
  citation?: LearnCitation;
  sources?: SourceArgType;
  access?: string[];
  overrides?: OverridesArgType;
  names?: string[];
  renames?: string[];
  breadcrumb: string[] | null;
  loggedOutLibraryLink: string | null;
  loggedInLibraryLink: string | null;
  data: LearnCacheEntityData;
  icons: string[];
  views: number;
  sections?: LearnSections;
  playlistPopulatedData?: {
    playlist: Record<string, { title: string }[]>;
  };
  playlist?: LearnCompletedPlaylist;
  osmosisPlaylist?: LearnCompletedPlaylist;
  libraryLink?: string;
}

interface LearnSectionsList {
  list: number[];
}

export interface LearnFormattedResource {
  tagID: number;
  title?: string;
  url?: string;
  thumb?: string;
  type?: string;
  src?: string;
}

export interface LearFormattedReference {
  title?: string;
  url?: string;
  type?: string;
  icon?: string;
  image?: {
    url: string;
  };
}

export interface LearnCks {
  title: string;
  isbn: string;
  thumb: string;
  sectionTitle?: string;
  sectionLink?: string;
  chapter?: Omit<CksChapter, 'sections'>;
}

export interface LearnCompleteAnatomy {
  title: string;
  links: string[];
}

export interface FirstAidConcept {
  [key: string]: unknown;
  ref: string;
  group?: string[];
}

export interface LearnifyReference {
  data: FirstAidBlockData[];
  learnifiedFirstAidReferences: Record<string, FirstAidConcept[]>;
}

export interface LearnSections {
  flashcards: LearnSectionsList;
  questions: Record<string, LearnSectionsList>;
  memory: LearnFormattedResource[];
  references: LearFormattedReference[];
  related: [];
  resources: NodeDataField[];
  learnifiedResources: LearnifyReference['learnifiedFirstAidReferences'][];
  definition?: string;
  summary: string[];
  notes?: NotesDataBlock;
  cks?: LearnCks[];
  ca?: LearnCompleteAnatomy;
}

export type LearnCache = {
  learnMap: Map<string, LearnCacheEntity>;
  learnMapByID: Map<number, LearnCacheEntity>;
  lastModifiedTime: number;
};
